import {inject, Injectable} from '@angular/core';
import {iReferenceDataLiquidityPoolRulesAbi} from 'dex-abi/contracts/IReferenceDataLiquidityPoolRules';
import {firstValueFrom} from 'rxjs';
import {Hex} from 'viem';
import {CommonBlockchainUtilService} from '../../components/dex/common-blockchain-util.service';
import {DEX_RATE_REF_SYMBOL} from '../../components/dex/dex-streaming/const';
import {DexMdSubjectService} from '../../components/dex/dex-streaming/subjects/dex-md-subject-service';
import {DexSecuritiesSubjectService} from '../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {ComputedTranche} from '../../components/dex/dex-streaming/types/computed/computed-tranche';
import {DeliverableId} from '../../components/dex/dex-streaming/types/deliverable';
import {TrancheId} from '../../components/dex/dex-streaming/types/tranche';
import {
    numToValueWithDel,
    ValueWithDel,
    ValueWithDelAndComments,
    vToDec
} from '../../components/dex/dex-streaming/utils';
import {TrancheDelRefBalanceService} from '../../components/dex/dex-tranche-utility/tranche-del-ref-balance.service';
import {AppConfigService} from '../../services/config.service';
import {DexKnownError} from './trade-execution/dex-known-errors';

interface FeeRuleCacheRec {
    trId: TrancheId;
    dId: DeliverableId;
    feeRule: FeeRule;
    ts: number;
}

interface FeeRule {
    trancheDeliverable: {
        poolTrancheId: bigint;
        deliverableId: bigint;
    }
    depositConstrainsRiskBps: bigint;
    depositIncreasesRiskBps: bigint;
    withdrawalConstrainsRiskBps: bigint;
    withdrawalIncreasesRiskBps: bigint;
    specificInstructionSurchargeBps: bigint;
}

interface ExpectedWithdrawableAsset {
    deliverableId: DeliverableId;
    percentageOfPoolBps: bigint;
    assetValue: bigint;
    assetQuantityToWithdraw?: bigint;
    assetValueToWithdraw?: bigint;
}

const FEE_BPS_DP = 4;
const ONE_HUNDRED_PERCENT_BPS = 10000n;
const ONE_HUNDRED_PERCENT = 100000000n;

@Injectable()
export class DexLpTradeFeeService {

    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly dexMdSubjectService = inject(DexMdSubjectService);
    private readonly trancheDelRefBalanceService = inject(TrancheDelRefBalanceService);
    private readonly commonBlockchainUtilService = inject(CommonBlockchainUtilService);
    private readonly appConfigService = inject(AppConfigService);

    private readonly feeRulesCache = new Map<string, FeeRuleCacheRec>();

    private readonly publicClient = this.commonBlockchainUtilService.getPublicClient();
    private readonly dexContractAddress = this.appConfigService.appConfig.dex.dexContractAddress;

    private readonly FEE_RULE_CACHE_TIMEOUT_MS = 1000 * 60 * 10; // 10 mins

    public async getDepositFee(
        trId: TrancheId,
        dId: DeliverableId,
        depositQty: ValueWithDel
    ): Promise<ValueWithDelAndComments> {
        const dRef = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);
        const refBalance = await firstValueFrom(
            this.trancheDelRefBalanceService.getRefCrossBalance(depositQty, dRef)
        );

        const feeRule = await this.getCachedFeeRule(trId, dId);

        if (!feeRule) {
            return {d: dRef, v: undefined};
        }

        const feeBps = feeRule.depositConstrainsRiskBps;

        const dDepositUsdQty = vToDec(refBalance.refBalance, refBalance.refBalanceDel.decimalPlaces);
        const dFeeBps = vToDec(feeBps, FEE_BPS_DP);
        const dFee = dDepositUsdQty.mul(dFeeBps).div(ONE_HUNDRED_PERCENT_BPS);

        const result: ValueWithDelAndComments = numToValueWithDel(dFee, dRef);

        // Adding comments
        const tr = this.dexMdSubjectService.trancheMapById.get(trId);
        const dTr = this.dexSecuritiesSubjectService.getDeliverableById(tr.unitDeliverableId);
        const d = this.dexSecuritiesSubjectService.getDeliverableById(dId);
        result.mathComments = [
            'Estimating Fee:',
            `Purchase ${dTr.symbol}/${d.symbol}`,
            `Rate ${refBalance.balanceDel.symbol}/${refBalance.rateDel.symbol}:`
            + ` ${vToDec(refBalance.rate, refBalance.rateDel.decimalPlaces).toFixed()}`,
            `Amount: ${vToDec(depositQty.v, depositQty.d.decimalPlaces).toFixed()} ${depositQty.d.symbol}`
            + ` = ${dDepositUsdQty.toFixed()} ${dRef.symbol}`,
            `Fee: ${dDepositUsdQty.toFixed()} × ${dFeeBps.toFixed()} bps ÷ ${ONE_HUNDRED_PERCENT_BPS}`
            + ` = ${dFee.toFixed()} ${dRef.symbol}`
        ].join('\n    ');

        return result;
    }

    public async getWithdrawalFee(
        tranche: ComputedTranche,
        lptQty: ValueWithDel,
        specificAssetWithdrawal: boolean = false
    ): Promise<ValueWithDel> {
        const dRef = this.dexSecuritiesSubjectService.getDeliverableBySymbol(DEX_RATE_REF_SYMBOL);

        // Get the expected withdrawable assets from tranche positions
        const expectedWithdrawableAssets = this.getExpectedWithdrawableAssets(tranche);

        if (!expectedWithdrawableAssets || expectedWithdrawableAssets.length === 0) {
            return {d: dRef, v: undefined};
        }

        // Calculate the expected withdrawal fee based on pool composition
        const expectedFeeBps = await this.calculateExpectedWithdrawalFee(
            tranche.raw.poolTrancheId,
            expectedWithdrawableAssets,
            specificAssetWithdrawal
        );

        if (expectedFeeBps === undefined) {
            return {d: dRef, v: undefined};
        }

        // Calculate the estimated asset value to withdraw
        const refBalance = await firstValueFrom(this.trancheDelRefBalanceService.getRefCrossBalance(lptQty, dRef));

        if (!refBalance) {
            return {d: dRef, v: undefined};
        }

        // Value of number of liquidity pool tokens being burnt
        const estimatedAssetValueToWithdraw = refBalance.refBalance;

        // Value of the fee
        const feeValue = (estimatedAssetValueToWithdraw * expectedFeeBps) / ONE_HUNDRED_PERCENT;

        return {d: dRef, v: feeValue};
    }

    private getExpectedWithdrawableAssets(tranche: ComputedTranche): ExpectedWithdrawableAsset[] {
        const positions = tranche.$computedPositions();
        const totalValue = tranche.$totalValue();

        if (!positions || !totalValue || totalValue === 0n) {
            return [];
        }

        const expectedWithdrawableAssets: ExpectedWithdrawableAsset[] = [];

        // Calculate percentage of each asset in the tranche
        for (const position of positions) {
            if (position.raw.valueDeliverableId === tranche.raw.unitDeliverableId) {
                continue; // Skip the LP token itself
            }

            const assetValue = BigInt(position.raw.portfolioValue);
            if (assetValue === 0n) {
                continue;
            }

            // Calculate percentage in basis points (1% = 100 bps, 100% = 10000 bps)
            const percentageOfPoolBps = (assetValue * ONE_HUNDRED_PERCENT) / totalValue;

            expectedWithdrawableAssets.push({
                deliverableId: position.raw.valueDeliverableId,
                percentageOfPoolBps,
                assetValue
            });
        }

        return expectedWithdrawableAssets;
    }

    private async calculateExpectedWithdrawalFee(
        trId: TrancheId,
        assetsToBeWithdrawn: ExpectedWithdrawableAsset[],
        specificAssetWithdrawal: boolean
    ): Promise<bigint | undefined> {
        let feeBps = 0n;
        let totalBps = 0n;

        for (const asset of assetsToBeWithdrawn) {
            const feeRule = await this.getCachedFeeRule(trId, asset.deliverableId);

            if (!feeRule) {
                return undefined;
            }

            // For Day 1 implementation, use withdrawalIncreasesRiskBps
            const withdrawalFeeBps = feeRule.withdrawalIncreasesRiskBps;

            // Add specific instruction surcharge if withdrawing specific asset
            const effectiveFeeBps = specificAssetWithdrawal
                ? withdrawalFeeBps + feeRule.specificInstructionSurchargeBps
                : withdrawalFeeBps;

            // Weighted fee calculation based on asset percentage in pool
            feeBps += (effectiveFeeBps * asset.percentageOfPoolBps) / ONE_HUNDRED_PERCENT;
            totalBps += asset.percentageOfPoolBps;
        }

        // Handle remainder to ensure we don't lose precision
        const remainder = ONE_HUNDRED_PERCENT - totalBps;
        return feeBps > 0 ? feeBps + remainder : feeBps;
    }

    private async getCachedFeeRule(trId: TrancheId, dId: DeliverableId): Promise<FeeRule | undefined> {
        const key = this.getFeeRuleCacheKey(trId, dId);
        let rec = this.feeRulesCache.get(key);
        const ts = Date.now();

        if (!rec || ts - rec.ts >= this.FEE_RULE_CACHE_TIMEOUT_MS ) {
            rec = {
                trId,
                dId,
                feeRule: await this.fetchFeeRule(trId, dId),
                ts
            };

            this.feeRulesCache.set(key, rec);
        }

        return rec.feeRule;
    }

    private async fetchFeeRule(trId: TrancheId, dId: DeliverableId): Promise<FeeRule | undefined> {
        try {
            return await this.publicClient.readContract({
                address:           this.dexContractAddress as Hex,
                abi:               iReferenceDataLiquidityPoolRulesAbi,
                functionName:      'getPoolTrancheDeliverableFeeRule',
                args:              [{poolTrancheId: BigInt(trId), deliverableId: BigInt(dId)}],
                authorizationList: undefined
            });
        } catch (error) {
            const parsedError = await DexKnownError.fromViem('getPoolTrancheDeliverableFeeRule', error);
            console.error(parsedError);

            return undefined;
        }
    }

    private getFeeRuleCacheKey(trId: TrancheId, dId: DeliverableId): string {
        return [
            trId,
            dId
        ].join(':');
    }

}
